import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DataGridView } from "@neumetric/blueprint-lib/components";
import { Pagination } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { riskActions, getAssetsList } from "../../store";

const PER_PAGE = Number(process.env.REACT_APP_INC_PER_PAGE || 9);
const FOOTER_H = 48;

const columns = [
  {
    dataIndex: "asset",
    title: "Asset",
    typeRepresentation: "TEXT",
    width: "50%",
    showTooltip: true,
  },
  {
    dataIndex: "asset_type",
    title: "Asset Type",
    typeRepresentation: "TEXT",
    width: "50%",
    showTooltip: true,
  },
];

const getPage = (search) => {
  const params = new URLSearchParams(search);
  const p = Number(params.get("page") || 1);
  return Number.isNaN(p) || p < 1 ? 1 : p;
};

export const AssetsTrackerScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const assetsList = useSelector(getAssetsList) || [];
  const totalCount = assetsList.length;

  const pageNo = getPage(location.search);
  const maxPage = Math.max(1, Math.ceil(totalCount / PER_PAGE));
  const safePageNo = Math.min(pageNo, maxPage);

  const rootRef = React.useRef(null);
  const [rootHeight, setRootHeight] = React.useState(500);

  React.useEffect(() => {
    dispatch(riskActions.fetchAssetList());
  }, [dispatch]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get("page")) {
      navigate(`${location.pathname}?page=1`, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  React.useLayoutEffect(() => {
    const compute = () => {
      if (!rootRef.current) return;
      const top = rootRef.current.getBoundingClientRect().top;
      const available = window.innerHeight - top - 12;
      setRootHeight(Math.max(220, available));
    };

    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  const pagedAssets = React.useMemo(() => {
    const start = (safePageNo - 1) * PER_PAGE;
    return assetsList.slice(start, start + PER_PAGE);
  }, [assetsList, safePageNo]);

  const handlePageChange = (p) => {
    navigate(`${location.pathname}?page=${p}`);
  };

  const showFooter = totalCount > PER_PAGE;

  return (
    <div
      ref={rootRef}
      className="col w-100 w-700-px mt-10"
      style={{ height: rootHeight, display: "flex", flexDirection: "column" }}
    >
      {/* Table region */}
      <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <DataGridView
          style={{ height: "100%" }}
          columns={columns}
          dataSource={pagedAssets}
          EmptyComponent="There are no assets right now!"
        />
      </div>

      {/* Pagination footer */}
      <div
        style={{
          height: showFooter ? FOOTER_H : 0,
          display: showFooter ? "flex" : "none",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Pagination
          current={safePageNo}
          pageSize={PER_PAGE}
          total={totalCount}
          showSizeChanger={false}
          onChange={handlePageChange}
        />
      </div>
    </div>
  );
};
